import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import styles from './TutorialAIVerifyLearning.mod.scss';
import { useIntl } from 'react-intl';
import { useBlockly } from '@modules/blockly/features/hooks';
import { CONFIDENCE_THRESHOLD, TRAIN_STEPS } from '@utils/hooks/machineLearning/params';
import { setMLTrainingStep } from '@modules/blockly/features/blocklySlice';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TutorialProgressV2 from '@sections/creabots/components/tutorialProgressV2';

const AIVerifyLearningTutorial5Step20 = ({ onNextStep, onPrevStep, currentStep, totalSteps }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const blocklyState = useBlockly();
  const [modalPage, setModalPage] = useState(0);
  const machineLearningState = blocklyState?.machineLearning || {};

  const intlPrefix = 'creabots.tutorials.ai.projects.smartTrashCan';
  const imgBaseUrl = '/images/creabots/ai/projects/smartTrashCan/steps/';

  const handleYes = () => {
    if (onNextStep) {
      onNextStep();
    }
  };

  const handleRestart = () => {
    setModalPage(0);
    dispatch(setMLTrainingStep(TRAIN_STEPS.CLASSIFY));
  };

  const handleNo = () => {
    setModalPage(2);
    dispatch(setMLTrainingStep(TRAIN_STEPS.TEACH));
  };

  useEffect(() => {
    if (machineLearningState?.prediction) {
      let classDetected = null;
      const detectionConfidences = machineLearningState.prediction || {};
      if (Object.keys(detectionConfidences).length > 0) {
        for (const sample of Object.keys(machineLearningState.samples)) {
          if (
            detectionConfidences[machineLearningState.samples[sample].name] &&
            (detectionConfidences[machineLearningState.samples[sample].name] || 0) * 100 >= CONFIDENCE_THRESHOLD
          ) {
            classDetected = sample;
          }
        }
      }

      if (modalPage === 0 && classDetected === 'class0') {
        setModalPage(1);
      }
      if (modalPage === 1 && classDetected === 'class1') {
        handleYes();
      }
    }
  }, [machineLearningState?.prediction]);

  const getStepContent = () => {
    switch (modalPage) {
      case 0:
        return {
          description: `${intlPrefix}.step21a`,
          image: `${imgBaseUrl}/step21.gif`,
        };
      case 1:
        return {
          description: `${intlPrefix}.step21b`,
          image: `${imgBaseUrl}/step21.gif`,
        };
      case 2:
        return {
          description: `${intlPrefix}.step21c`,
          image: `${imgBaseUrl}/step21c.png`,
        };
      default:
        return {
          description: `${intlPrefix}.step21a`,
          image: `${imgBaseUrl}/step21.gif`,
        };
    }
  };

  const stepContent = getStepContent();
  const iaStep = true;

  return (
    <div className={styles.tutorialAIVerifyLearning}>
      <>
        <div className={styles.titleContainer}>
          <div className={styles.title}>
            <AssignmentOutlinedIcon /> {intl.formatMessage({ id: 'creabots.tutorialTitle' })}
          </div>
        </div>
        <div className={styles.instructionsContainer}>
          <div dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: stepContent.description }) }} />
          {modalPage === 0 && (
            <div className={styles.buttons}>
              <p>{intl.formatMessage({ id: 'creabots.tutorials.ai.didItLearn' })}</p>
              <button
                type="button"
                onClick={() => handleNo()}
                className={clsx('btn', 'btn-secondary', styles.secondaryButton)}
              >
                {intl.formatMessage({ id: 'creabots.tutorials.initiation.verifyBoard.btnNo' })}
              </button>
              <button
                type="button"
                onClick={handleYes}
                className={clsx('btn', 'btn-primary', 'main-btn', styles.primaryButton)}
              >
                {intl.formatMessage({ id: 'creabots.tutorials.initiation.verifyBoard.btnYes' })}
              </button>
            </div>
          )}
          {modalPage === 1 && (
            <div className={styles.buttons}>
              <p>{intl.formatMessage({ id: 'creabots.tutorials.ai.didItLearn' })}</p>
              <button
                type="button"
                onClick={() => handleNo()}
                className={clsx('btn', 'btn-secondary', styles.secondaryButton)}
              >
                {intl.formatMessage({ id: 'creabots.tutorials.initiation.verifyBoard.btnNo' })}
              </button>
              <button
                type="button"
                onClick={handleYes}
                className={clsx('btn', 'btn-primary', 'main-btn', styles.primaryButton)}
              >
                {intl.formatMessage({ id: 'creabots.tutorials.initiation.verifyBoard.btnYes' })}
              </button>
            </div>
          )}
          {modalPage === 2 && machineLearningState.modelTrained && (
            <div className={styles.buttons}>
              <button
                type="button"
                onClick={handleRestart}
                className={clsx('btn', 'btn-primary', 'main-btn', styles.primaryButton)}
              >
                {intl.formatMessage({ id: 'creabots.tutorials.ai.readyToContinue' })}
              </button>
            </div>
          )}
          <img src={stepContent.image} alt="Tutorial step" />
        </div>
        <div className={styles.progressContainer}>
          <TutorialProgressV2
            iaStep={iaStep}
            currentStep={currentStep + 1}
            totalStep={totalSteps}
            onNextStep={onNextStep}
            onPrevStep={onPrevStep}
          />
        </div>
      </>
    </div>
  );
};

export default AIVerifyLearningTutorial5Step20;
